import { useMemo } from 'react'
import { MODULES } from '../../../content'
import type { ModuleData } from '../../../content'
import {
  getAllModuleTrends,
  getModuleTrendDelta,
} from '../../../utils/activityHelpers'
import type { LearnerAccount } from '../../../accounts'
import type { AppState } from '../../../app/types'

export interface ModuleGrowthEntry {
  module: ModuleData
  mastery: number
  trend: string
  delta: number
}

export interface GrowthData {
  modules: ModuleGrowthEntry[]
  overallMastery: number
  strengths: ModuleGrowthEntry[]
  areasForGrowth: ModuleGrowthEntry[]
}

/**
 * useGrowthData — Shared growth computation for parent portal pages
 *
 * Extracts the duplicated growthData useMemo from GrowthPage and VoortgangPage
 * into a single reusable hook. Pure computation, no side effects.
 */
export function useGrowthData(
  selectedChild: LearnerAccount | undefined,
  childData: AppState | null,
  selectedGroup: string,
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number,
): GrowthData | null {
  const groupNum = parseInt(selectedGroup) || 6

  return useMemo(() => {
    if (!selectedChild || !childData) return null

    const trends = getAllModuleTrends(childData.familyMastery || {}, groupNum)

    const modules = MODULES.map((module) => {
      const mastery = getLearnerModuleMastery(selectedChild.id, module.id)
      const delta = getModuleTrendDelta(
        childData.familyMastery || {},
        groupNum,
        module.id,
      )
      return {
        module,
        mastery,
        trend: trends[module.id] || 'neutral',
        delta,
      }
    })
      .filter((m) => m.mastery > 0)
      .sort((a, b) => b.mastery - a.mastery)

    const overallMastery =
      modules.length > 0
        ? Math.round(
            modules.reduce((sum, m) => sum + m.mastery, 0) / modules.length,
          )
        : 0

    const strengths = modules.filter((m) => m.mastery >= 60).slice(0, 3)
    const areasForGrowth = [...modules]
      .reverse()
      .filter((m) => m.mastery < 50)
      .slice(0, 3)

    return {
      modules,
      overallMastery,
      strengths,
      areasForGrowth,
    }
  }, [
    selectedChild,
    childData,
    selectedGroup,
    getLearnerModuleMastery,
    groupNum,
  ])
}
